import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { themesAPI } from '../../lib/themesApi';
import { useTheme } from '../../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
    Palette,
    ArrowLeft,
    Upload,
    Trash2,
    RefreshCw,
    Wand2,
    Check,
    Loader2,
    Image,
    Eye,
} from 'lucide-react';

const COLOR_PRESETS = [
    { name: 'Azul', primary: '#3b82f6', secondary: '#6366f1' },
    { name: 'Verde', primary: '#10b981', secondary: '#14b8a6' },
    { name: 'Rojo', primary: '#ef4444', secondary: '#f97316' },
    { name: 'Púrpura', primary: '#8b5cf6', secondary: '#a855f7' },
    { name: 'Rosa', primary: '#ec4899', secondary: '#f472b6' },
    { name: 'Amarillo', primary: '#eab308', secondary: '#f59e0b' },
];

export default function ThemeConfigPage() {
    const navigate = useNavigate();
    const { theme, updateTheme, refreshTheme } = useTheme();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState(null);
    const [extractedColors, setExtractedColors] = useState([]);
    const [previewMode, setPreviewMode] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        try {
            setLoading(true);
            const res = await themesAPI.getConfig();
            setConfig(res.data);
            if (res.data.extracted_colors) {
                setExtractedColors(res.data.extracted_colors);
            }
        } catch (err) {
            toast.error('Error al cargar configuración');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateTheme(config);
            toast.success('Tema guardado correctamente');
        } catch (err) {
            toast.error('Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleUploadLogo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error('El logo es demasiado grande. Máximo 2MB');
            return;
        }

        setSaving(true);
        try {
            const res = await themesAPI.uploadLogo(file);
            toast.success('Logo subido correctamente');
            
            if (res.data.extracted_colors) {
                setExtractedColors(res.data.extracted_colors);
            }
            
            if (res.data.suggested_theme) {
                setConfig(prev => ({
                    ...prev,
                    logo_url: res.data.logo_url,
                }));
            }
            
            loadConfig();
        } catch (err) {
            toast.error('Error al subir logo');
        } finally {
            setSaving(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleApplySuggested = async () => {
        setSaving(true);
        try {
            const res = await themesAPI.applySuggestedTheme();
            setConfig(prev => ({
                ...prev,
                primary_color: res.data.primary_color,
                secondary_color: res.data.secondary_color,
                accent_color: res.data.accent_color,
            }));
            refreshTheme();
            toast.success('Tema aplicado desde el logo');
        } catch (err) {
            toast.error('Error al aplicar tema');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm('¿Resetear el tema a valores por defecto?')) return;
        
        setSaving(true);
        try {
            await themesAPI.resetTheme();
            refreshTheme();
            loadConfig();
            toast.success('Tema reseteado');
        } catch (err) {
            toast.error('Error al resetear');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteLogo = async () => {
        if (!window.confirm('¿Eliminar el logo?')) return;
        
        setSaving(true);
        try {
            await themesAPI.deleteLogo();
            setConfig(prev => ({ ...prev, logo_url: null }));
            setExtractedColors([]);
            toast.success('Logo eliminado');
        } catch (err) {
            toast.error('Error al eliminar logo');
        } finally {
            setSaving(false);
        }
    };

    const applyPreset = (preset) => {
        setConfig(prev => ({
            ...prev,
            primary_color: preset.primary,
            secondary_color: preset.secondary,
        }));
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Palette className="h-6 w-6" />
                            Personalización de Tema
                        </h1>
                        <p className="text-muted-foreground">Configura la apariencia de tu empresa</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={handleReset} disabled={saving}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Resetear
                    </Button>
                    <Button onClick={handleSave} disabled={saving} data-testid="save-theme-btn">
                        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                        Guardar
                    </Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Logo Upload */}
                <Card className="lg:col-span-1">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Image className="h-5 w-5" />
                            Logo de Empresa
                        </CardTitle>
                        <CardDescription>
                            Sube tu logo y extraeremos los colores automáticamente
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Preview del logo */}
                        <div className="aspect-video border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/30 overflow-hidden">
                            {config?.logo_url ? (
                                <img 
                                    src={`${process.env.REACT_APP_BACKEND_URL}${config.logo_url}`}
                                    alt="Logo de empresa"
                                    className="max-h-full max-w-full object-contain p-4"
                                />
                            ) : (
                                <div className="text-center text-muted-foreground p-4">
                                    <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">Sin logo</p>
                                </div>
                            )}
                        </div>

                        {/* Botones de acción */}
                        <div className="flex gap-2">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".png,.jpg,.jpeg,.svg,.webp"
                                className="hidden"
                                onChange={handleUploadLogo}
                            />
                            <Button 
                                variant="outline" 
                                className="flex-1"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={saving}
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                Subir Logo
                            </Button>
                            {config?.logo_url && (
                                <Button 
                                    variant="outline" 
                                    size="icon"
                                    onClick={handleDeleteLogo}
                                    disabled={saving}
                                >
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                            )}
                        </div>

                        {/* Colores extraídos */}
                        {extractedColors.length > 0 && (
                            <div className="space-y-3 pt-4 border-t">
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm">Colores detectados</Label>
                                    <Button 
                                        size="sm" 
                                        variant="outline"
                                        onClick={handleApplySuggested}
                                        disabled={saving}
                                    >
                                        <Wand2 className="h-3 w-3 mr-1" />
                                        Aplicar
                                    </Button>
                                </div>
                                <div className="flex gap-2">
                                    {extractedColors.map((color, i) => (
                                        <button
                                            key={i}
                                            className="w-10 h-10 rounded-lg border-2 border-transparent hover:border-foreground transition-all shadow-sm"
                                            style={{ backgroundColor: color }}
                                            onClick={() => setConfig(prev => ({ ...prev, primary_color: color }))}
                                            title={color}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Color Configuration */}
                <Card className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle>Colores del Tema</CardTitle>
                        <CardDescription>
                            Personaliza los colores principales de la interfaz
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Presets */}
                        <div className="space-y-2">
                            <Label>Presets de color</Label>
                            <div className="flex flex-wrap gap-2">
                                {COLOR_PRESETS.map((preset) => (
                                    <button
                                        key={preset.name}
                                        className="flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-muted transition-colors"
                                        onClick={() => applyPreset(preset)}
                                    >
                                        <div 
                                            className="w-4 h-4 rounded-full"
                                            style={{ backgroundColor: preset.primary }}
                                        />
                                        <span className="text-sm">{preset.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Color Inputs */}
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Color Primario</Label>
                                <div className="flex gap-2">
                                    <input
                                        type="color"
                                        value={config?.primary_color || '#3b82f6'}
                                        onChange={(e) => setConfig(prev => ({ ...prev, primary_color: e.target.value }))}
                                        className="w-12 h-10 rounded border cursor-pointer"
                                    />
                                    <Input
                                        value={config?.primary_color || '#3b82f6'}
                                        onChange={(e) => setConfig(prev => ({ ...prev, primary_color: e.target.value }))}
                                        className="font-mono"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Color Secundario</Label>
                                <div className="flex gap-2">
                                    <input
                                        type="color"
                                        value={config?.secondary_color || '#6366f1'}
                                        onChange={(e) => setConfig(prev => ({ ...prev, secondary_color: e.target.value }))}
                                        className="w-12 h-10 rounded border cursor-pointer"
                                    />
                                    <Input
                                        value={config?.secondary_color || '#6366f1'}
                                        onChange={(e) => setConfig(prev => ({ ...prev, secondary_color: e.target.value }))}
                                        className="font-mono"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Color de Acento</Label>
                                <div className="flex gap-2">
                                    <input
                                        type="color"
                                        value={config?.accent_color || '#10b981'}
                                        onChange={(e) => setConfig(prev => ({ ...prev, accent_color: e.target.value }))}
                                        className="w-12 h-10 rounded border cursor-pointer"
                                    />
                                    <Input
                                        value={config?.accent_color || '#10b981'}
                                        onChange={(e) => setConfig(prev => ({ ...prev, accent_color: e.target.value }))}
                                        className="font-mono"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Color de Texto</Label>
                                <div className="flex gap-2">
                                    <input
                                        type="color"
                                        value={config?.text_color || '#1f2937'}
                                        onChange={(e) => setConfig(prev => ({ ...prev, text_color: e.target.value }))}
                                        className="w-12 h-10 rounded border cursor-pointer"
                                    />
                                    <Input
                                        value={config?.text_color || '#1f2937'}
                                        onChange={(e) => setConfig(prev => ({ ...prev, text_color: e.target.value }))}
                                        className="font-mono"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Style Options */}
                        <div className="grid sm:grid-cols-3 gap-4 pt-4 border-t">
                            <div className="space-y-2">
                                <Label>Estilo de Sidebar</Label>
                                <Select 
                                    value={config?.sidebar_style || 'light'} 
                                    onValueChange={(v) => setConfig(prev => ({ ...prev, sidebar_style: v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="light">Claro</SelectItem>
                                        <SelectItem value="dark">Oscuro</SelectItem>
                                        <SelectItem value="colored">Con Color</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Estilo de Header</Label>
                                <Select 
                                    value={config?.header_style || 'default'} 
                                    onValueChange={(v) => setConfig(prev => ({ ...prev, header_style: v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="default">Por defecto</SelectItem>
                                        <SelectItem value="colored">Con Color</SelectItem>
                                        <SelectItem value="gradient">Gradiente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Bordes Redondeados</Label>
                                <Select 
                                    value={config?.border_radius || 'medium'} 
                                    onValueChange={(v) => setConfig(prev => ({ ...prev, border_radius: v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Sin redondeo</SelectItem>
                                        <SelectItem value="small">Pequeño</SelectItem>
                                        <SelectItem value="medium">Medio</SelectItem>
                                        <SelectItem value="large">Grande</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Preview */}
                        <div className="pt-4 border-t">
                            <Label className="mb-3 block">Vista Previa</Label>
                            <div 
                                className="p-6 rounded-lg border"
                                style={{ backgroundColor: config?.background_color || '#ffffff' }}
                            >
                                <div className="flex gap-4 mb-4">
                                    <button
                                        className="px-4 py-2 rounded-md text-white font-medium"
                                        style={{ 
                                            backgroundColor: config?.primary_color || '#3b82f6',
                                            borderRadius: config?.border_radius === 'none' ? '0' : 
                                                         config?.border_radius === 'small' ? '0.25rem' :
                                                         config?.border_radius === 'large' ? '1rem' : '0.5rem'
                                        }}
                                    >
                                        Botón Primario
                                    </button>
                                    <button
                                        className="px-4 py-2 rounded-md text-white font-medium"
                                        style={{ 
                                            backgroundColor: config?.secondary_color || '#6366f1',
                                            borderRadius: config?.border_radius === 'none' ? '0' : 
                                                         config?.border_radius === 'small' ? '0.25rem' :
                                                         config?.border_radius === 'large' ? '1rem' : '0.5rem'
                                        }}
                                    >
                                        Botón Secundario
                                    </button>
                                    <button
                                        className="px-4 py-2 rounded-md text-white font-medium"
                                        style={{ 
                                            backgroundColor: config?.accent_color || '#10b981',
                                            borderRadius: config?.border_radius === 'none' ? '0' : 
                                                         config?.border_radius === 'small' ? '0.25rem' :
                                                         config?.border_radius === 'large' ? '1rem' : '0.5rem'
                                        }}
                                    >
                                        Acento
                                    </button>
                                </div>
                                <p style={{ color: config?.text_color || '#1f2937' }}>
                                    Este es un texto de ejemplo con el color configurado.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
